import { Suspense, useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Loader2, Search, Send, UserCheck } from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import {
  CATEGORIA_TAREFA_LABEL, PRIORIDADE_LABEL, STATUS_TAREFA_LABEL,
  type CategoriaTarefa, type PrioridadeTarefa, type StatusTarefa,
  type TarefaComentario, type TarefaDelegacao,
} from '@/lib/types'
import { formatarData } from '@/lib/datas'
import { getMembros, type MembroComPerfil } from '@/services/equipe'
import { listPacientes } from '@/services/pacientes'
import {
  atualizarTarefa, comentarTarefa, criarTarefa, delegarTarefa,
  getTarefa, mudarStatusTarefa, type TarefaComRelacoes,
} from '@/services/tarefas'

function FormPendencia() {
  const { current, user, papel } = useAuth()
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const editId = params.get('id')
  const pacientePre = params.get('paciente')

  const [tarefa, setTarefa] = useState<TarefaComRelacoes | null>(null)
  const [comentarios, setComentarios] = useState<TarefaComentario[]>([])
  const [delegacoes, setDelegacoes] = useState<TarefaDelegacao[]>([])
  const [membros, setMembros] = useState<MembroComPerfil[]>([])

  const [titulo, setTitulo] = useState('')
  const [descricao, setDescricao] = useState('')
  const [categoria, setCategoria] = useState<CategoriaTarefa>('personalizada')
  const [prioridade, setPrioridade] = useState<PrioridadeTarefa>('normal')
  const [prazo, setPrazo] = useState('')
  const [responsavelId, setResponsavelId] = useState('')
  const [pacienteId, setPacienteId] = useState(pacientePre ?? '')
  const [pacienteNome, setPacienteNome] = useState('')
  const [buscaPaciente, setBuscaPaciente] = useState('')
  const [sugestoes, setSugestoes] = useState<{ id: string; nome: string }[]>([])
  const [visibilidade, setVisibilidade] = useState<'equipe' | 'medicos'>('equipe')

  const [novoComentario, setNovoComentario] = useState('')
  const [delegarPara, setDelegarPara] = useState('')
  const [instrucao, setInstrucao] = useState('')
  const [prazoDelegacao, setPrazoDelegacao] = useState('')
  const [mostrarDelegar, setMostrarDelegar] = useState(false)

  const [erro, setErro] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [loading, setLoading] = useState(true)

  const carregar = useCallback(async () => {
    if (!current) return
    try {
      const ms = await getMembros(current.workspace.id)
      setMembros(ms.filter(m => m.ativo && m.user_id && m.convite_status === 'aceito'))
      if (editId) {
        const r = await getTarefa(current.workspace.id, editId)
        if (r) {
          setTarefa(r.tarefa)
          setComentarios(r.comentarios)
          setDelegacoes(r.delegacoes)
          setTitulo(r.tarefa.titulo)
          setDescricao(r.tarefa.descricao ?? '')
          setCategoria(r.tarefa.categoria)
          setPrioridade(r.tarefa.prioridade)
          setPrazo(r.tarefa.prazo ?? '')
          setResponsavelId(r.tarefa.responsavel_user_id ?? '')
          setPacienteId(r.tarefa.paciente_id ?? '')
          setPacienteNome(r.tarefa.paciente_nome ?? '')
          setVisibilidade(r.tarefa.visibilidade)
        }
      } else if (pacientePre) {
        const ps = await listPacientes(current.workspace.id, { situacao: 'todas' })
        const p = ps.find(x => x.id === pacientePre)
        if (p) setPacienteNome(p.nome)
      }
    } finally {
      setLoading(false)
    }
  }, [current, editId, pacientePre])

  useEffect(() => { carregar() }, [carregar])

  useEffect(() => {
    if (!current || buscaPaciente.trim().length < 2) { setSugestoes([]); return }
    const t = setTimeout(async () => {
      const ps = await listPacientes(current.workspace.id, { busca: buscaPaciente, situacao: 'todas' })
      setSugestoes(ps.slice(0, 8).map(p => ({ id: p.id, nome: p.nome })))
    }, 250)
    return () => clearTimeout(t)
  }, [current, buscaPaciente])

  const nomeMembro = useCallback(
    (uid: string | null) => membros.find(m => m.user_id === uid)?.perfil?.nome ?? '—',
    [membros]
  )

  const aberta = tarefa ? !['concluida', 'cancelada'].includes(tarefa.status) : true

  async function salvar(e: React.FormEvent) {
    e.preventDefault()
    if (!current || !user) return
    if (titulo.trim().length < 3) { setErro('Dê um título à pendência.'); return }
    setBusy(true)
    setErro(null)
    const dados = {
      titulo: titulo.trim(),
      descricao: descricao || null,
      categoria,
      prioridade,
      prazo: prazo || null,
      responsavel_user_id: responsavelId || null,
      paciente_id: pacienteId || null,
      visibilidade,
    }
    try {
      if (editId) {
        await atualizarTarefa({ workspaceId: current.workspace.id, id: editId, dados })
        await carregar()
        setBusy(false)
      } else {
        await criarTarefa({ workspaceId: current.workspace.id, dados, criadorUserId: user.id })
        navigate('/pendencias')
      }
    } catch {
      setErro('Não foi possível salvar a pendência.')
      setBusy(false)
    }
  }

  async function mudarStatus(novo: StatusTarefa) {
    if (!current || !tarefa) return
    let motivo: string | undefined
    if (novo === 'cancelada') {
      motivo = window.prompt('Motivo do cancelamento (obrigatório — a pendência não some, fica registrada):') ?? undefined
      if (!motivo?.trim()) return
    }
    setBusy(true)
    try {
      await mudarStatusTarefa({
        workspaceId: current.workspace.id,
        tarefa,
        novoStatus: novo,
        motivoCancelamento: motivo,
        notificarUserId: tarefa.criado_por && tarefa.criado_por !== user?.id ? tarefa.criado_por : null,
      })
      await carregar()
    } finally {
      setBusy(false)
    }
  }

  async function delegar(e: React.FormEvent) {
    e.preventDefault()
    if (!current || !tarefa || !delegarPara) return
    const alvo = membros.find(m => m.user_id === delegarPara)
    if (!alvo) return
    setBusy(true)
    try {
      await delegarTarefa({
        workspaceId: current.workspace.id,
        tarefa,
        paraUserId: delegarPara,
        paraNome: alvo.perfil?.nome ?? 'membro',
        paraEhMedico: alvo.papel === 'medico_titular' || alvo.papel === 'medico_colaborador',
        instrucao: instrucao || null,
        prazo: prazoDelegacao || null,
      })
      setMostrarDelegar(false)
      setInstrucao('')
      setPrazoDelegacao('')
      await carregar()
    } finally {
      setBusy(false)
    }
  }

  async function comentar(e: React.FormEvent) {
    e.preventDefault()
    if (!current || !editId || !novoComentario.trim()) return
    await comentarTarefa({ workspaceId: current.workspace.id, tarefaId: editId, texto: novoComentario })
    setNovoComentario('')
    await carregar()
  }

  if (loading) {
    return (
      <div className="card" style={{ padding: 32, textAlign: 'center', color: 'var(--muted)' }}>
        <Loader2 size={22} style={{ animation: 'spin 1s linear infinite' }} aria-hidden />
        <style>{'@keyframes spin{to{transform:rotate(360deg)}}'}</style>
      </div>
    )
  }

  return (
    <div>
      <h1 className="page-title" style={{ marginBottom: 6 }}>{editId ? 'Pendência' : 'Nova pendência'}</h1>
      {tarefa && (
        <div style={{ display: 'flex', gap: 6, marginBottom: 14, flexWrap: 'wrap' }}>
          <span className={`badge ${tarefa.status === 'concluida' ? 'badge-ok' : tarefa.status === 'cancelada' ? 'badge-muted' : 'badge-info'}`}>
            {STATUS_TAREFA_LABEL[tarefa.status]}
          </span>
          {tarefa.motivo_cancelamento && (
            <span className="badge badge-muted">Motivo: {tarefa.motivo_cancelamento}</span>
          )}
        </div>
      )}

      <form onSubmit={salvar} className="card" style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 14, marginBottom: 14 }}>
        <div>
          <label className="field-label" htmlFor="t-titulo">O que precisa ser feito? *</label>
          <input id="t-titulo" required className="field-input" value={titulo} onChange={e => setTitulo(e.target.value)} disabled={!aberta} />
        </div>
        <div>
          <label className="field-label" htmlFor="t-desc">Detalhes</label>
          <textarea id="t-desc" className="field-input" rows={2} style={{ resize: 'vertical' }} value={descricao} onChange={e => setDescricao(e.target.value)} disabled={!aberta} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
          <div>
            <label className="field-label" htmlFor="t-cat">Categoria</label>
            <select id="t-cat" className="field-input" value={categoria} onChange={e => setCategoria(e.target.value as CategoriaTarefa)} disabled={!aberta}>
              {(Object.keys(CATEGORIA_TAREFA_LABEL) as CategoriaTarefa[]).map(c => (
                <option key={c} value={c}>{CATEGORIA_TAREFA_LABEL[c]}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="field-label" htmlFor="t-prio">Prioridade</label>
            <select id="t-prio" className="field-input" value={prioridade} onChange={e => setPrioridade(e.target.value as PrioridadeTarefa)} disabled={!aberta}>
              {(['baixa', 'normal', 'alta', 'urgente'] as PrioridadeTarefa[]).map(p => (
                <option key={p} value={p}>{PRIORIDADE_LABEL[p]}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="field-label" htmlFor="t-prazo">Prazo</label>
            <input id="t-prazo" type="date" className="field-input" value={prazo} onChange={e => setPrazo(e.target.value)} disabled={!aberta} />
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
          <div>
            <label className="field-label" htmlFor="t-resp">Responsável</label>
            <select id="t-resp" className="field-input" value={responsavelId} onChange={e => setResponsavelId(e.target.value)} disabled={!aberta}>
              <option value="">Sem responsável definido</option>
              {membros.map(m => (
                <option key={m.user_id} value={m.user_id!}>{m.perfil?.nome ?? m.convite_email}</option>
              ))}
            </select>
          </div>
          {(papel === 'medico_titular' || papel === 'medico_colaborador') && (
            <div>
              <label className="field-label" htmlFor="t-vis">Visibilidade</label>
              <select id="t-vis" className="field-input" value={visibilidade} onChange={e => setVisibilidade(e.target.value as typeof visibilidade)} disabled={!aberta}>
                <option value="equipe">Toda a equipe</option>
                <option value="medicos">Somente médicos</option>
              </select>
            </div>
          )}
        </div>

        <div>
          <label className="field-label" htmlFor="t-paciente">Paciente (opcional)</label>
          {pacienteId ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span className="badge badge-info" style={{ fontSize: 13.5, padding: '8px 12px' }}>{pacienteNome}</span>
              {aberta && (
                <button type="button" className="btn-secondary" style={{ minHeight: 34, padding: '4px 10px', fontSize: 13 }} onClick={() => { setPacienteId(''); setPacienteNome('') }}>
                  Remover
                </button>
              )}
            </div>
          ) : (
            <div style={{ position: 'relative' }}>
              <Search size={16} aria-hidden style={{ position: 'absolute', left: 12, top: 14, color: 'var(--muted)' }} />
              <input
                id="t-paciente" className="field-input" style={{ paddingLeft: 36 }}
                placeholder="Vincular a um paciente"
                value={buscaPaciente} onChange={e => setBuscaPaciente(e.target.value)}
                autoComplete="off" disabled={!aberta}
              />
              {sugestoes.length > 0 && (
                <div className="card" style={{ position: 'absolute', zIndex: 20, left: 0, right: 0, top: 48, boxShadow: 'var(--shadow-float)', overflow: 'hidden' }}>
                  {sugestoes.map(s => (
                    <button
                      key={s.id} type="button"
                      style={{ display: 'block', width: '100%', textAlign: 'left', padding: '11px 14px', background: 'none', border: 'none', fontSize: 14, borderBottom: '1px solid var(--border)' }}
                      onClick={() => { setPacienteId(s.id); setPacienteNome(s.nome); setSugestoes([]); setBuscaPaciente('') }}
                    >
                      {s.nome}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {erro && <div className="field-error" role="alert">{erro}</div>}

        {aberta && (
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button type="submit" className="btn-primary" disabled={busy}>
              {busy && <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} aria-hidden />}
              Salvar
            </button>
            {editId && tarefa && (
              <>
                <button type="button" className="btn-secondary" disabled={busy} onClick={() => setMostrarDelegar(v => !v)}>
                  <UserCheck size={15} aria-hidden /> Delegar
                </button>
                <button type="button" className="btn-secondary" disabled={busy} onClick={() => mudarStatus('concluida')}>
                  Concluir
                </button>
                <button type="button" className="btn-secondary" disabled={busy} onClick={() => mudarStatus('cancelada')}>
                  Cancelar pendência
                </button>
              </>
            )}
            {!editId && (
              <button type="button" className="btn-secondary" onClick={() => navigate(-1)}>Voltar</button>
            )}
          </div>
        )}
      </form>

      {mostrarDelegar && tarefa && aberta && (
        <form onSubmit={delegar} className="card" style={{ padding: 18, marginBottom: 14, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <strong style={{ fontSize: 14.5 }}>Delegar pendência</strong>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
            <div>
              <label className="field-label" htmlFor="d-para">Para *</label>
              <select id="d-para" required className="field-input" value={delegarPara} onChange={e => setDelegarPara(e.target.value)}>
                <option value="">Escolha um membro</option>
                {membros.filter(m => m.user_id !== user?.id).map(m => (
                  <option key={m.user_id} value={m.user_id!}>{m.perfil?.nome ?? m.convite_email}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="field-label" htmlFor="d-prazo">Prazo</label>
              <input id="d-prazo" type="date" className="field-input" value={prazoDelegacao} onChange={e => setPrazoDelegacao(e.target.value)} />
            </div>
          </div>
          <div>
            <label className="field-label" htmlFor="d-inst">Instrução</label>
            <input id="d-inst" className="field-input" value={instrucao} onChange={e => setInstrucao(e.target.value)} placeholder="Ex.: ligar para a paciente e confirmar o exame" />
          </div>
          <div>
            <button type="submit" className="btn-primary" disabled={busy || !delegarPara}>Delegar</button>
          </div>
        </form>
      )}

      {editId && delegacoes.length > 0 && (
        <div className="card" style={{ padding: 18, marginBottom: 14 }}>
          <strong style={{ fontSize: 14.5, display: 'block', marginBottom: 10 }}>Trilha de delegação</strong>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {delegacoes.map(d => (
              <div key={d.id} style={{ fontSize: 13.5, borderLeft: '3px solid var(--primary-soft)', paddingLeft: 10 }}>
                <div>
                  <strong>{nomeMembro(d.de_user_id)}</strong> → <strong>{nomeMembro(d.para_user_id)}</strong>
                  {d.prazo && <span style={{ color: 'var(--muted)' }}> · prazo {formatarData(d.prazo)}</span>}
                </div>
                {d.instrucao && <div style={{ color: 'var(--text-secondary)' }}>&ldquo;{d.instrucao}&rdquo;</div>}
                <div style={{ fontSize: 12, color: 'var(--muted)' }}>
                  {new Date(d.created_at).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {editId && (
        <div className="card" style={{ padding: 18 }}>
          <strong style={{ fontSize: 14.5, display: 'block', marginBottom: 10 }}>Comentários e histórico</strong>
          {comentarios.length === 0 ? (
            <div style={{ fontSize: 13.5, color: 'var(--muted)', marginBottom: 10 }}>Nenhum registro ainda.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
              {comentarios.map(c => (
                <div key={c.id} style={{ fontSize: 13.5, background: c.sistema ? 'var(--bg)' : 'var(--card)', border: '1px solid var(--border)', borderRadius: 8, padding: '9px 12px' }}>
                  <div style={{ color: c.sistema ? 'var(--muted)' : 'var(--text)' }}>
                    {c.sistema ? '⚙ ' : ''}{c.texto}
                  </div>
                  <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 3 }}>
                    {!c.sistema && `${nomeMembro(c.autor_user_id)} · `}
                    {new Date(c.created_at).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                  </div>
                </div>
              ))}
            </div>
          )}
          <form onSubmit={comentar} style={{ display: 'flex', gap: 8 }}>
            <input
              className="field-input" placeholder="Adicionar comentário…"
              value={novoComentario} onChange={e => setNovoComentario(e.target.value)}
              aria-label="Novo comentário"
            />
            <button type="submit" className="btn-primary" style={{ minHeight: 44, flexShrink: 0 }} disabled={!novoComentario.trim()} aria-label="Enviar comentário">
              <Send size={16} aria-hidden />
            </button>
          </form>
        </div>
      )}

      {editId && tarefa && aberta && (
        <div style={{ marginTop: 14, display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ fontSize: 13, color: 'var(--muted)' }}>Mover para:</span>
          {(['em_analise', 'aguardando_medico', 'aguardando_secretaria', 'aguardando_paciente', 'aguardando_exame', 'programada'] as StatusTarefa[])
            .filter(s => s !== tarefa.status)
            .map(s => (
              <button key={s} className="btn-secondary" style={{ minHeight: 32, padding: '4px 10px', fontSize: 12.5 }} disabled={busy} onClick={() => mudarStatus(s)}>
                {STATUS_TAREFA_LABEL[s]}
              </button>
            ))}
        </div>
      )}
      <div style={{ marginTop: 14 }}>
        <Link to="/pendencias" style={{ fontSize: 13.5, color: 'var(--primary-deep)', fontWeight: 600 }}>← Voltar para pendências</Link>
      </div>
    </div>
  )
}

export default function NovaPendenciaPage() {
  // useSearchParams exige Suspense no build de produção
  return (
    <Suspense fallback={null}>
      <FormPendencia />
    </Suspense>
  )
}
